// @flow

import { maxValue } from './utils/array.util';
import { formatDate } from './utils/date.utils';

export type ColumnType = 'bar' | 'line' | 'x' | 'area';

export type ChartData = {
    // TODO: validate input data
    columns: Array<Array<any>>,
    types: { [id: string]: ColumnType },
    names: { [id: string]: string },
    colors: { [id: string]: string }
}

export class ColumnModel {
    key: string;
    data: Array<number>;
    type: ColumnType;
    name: string;
    color: string;
    max: number;
    _disabled: ?boolean;
    _opacity: number;
    _selectionMax: number;
    constructor (key: string, data: Array<number>, type: ColumnType, name: string, color: string) {
        this.key = key;
        this.data = data;
        this.type = type;
        this.name = name;
        this.color = color;
        this.max = maxValue(this.data);
        this._opacity = 1;
    }
    getMaxColumnValue (from, to) {
        const selection = this.data.slice(from, to);
        return maxValue(selection);
    }
}

export class RowModel {
    key: string;
    data: Array<number>;
    type: ColumnType;
    dates: Array<string>;

    constructor (key: string, data: Array<number>, type: ColumnType) {
        this.key = key;
        this.data = data;
        this.type = type;
        this.dates = this.data.map(timestamp => formatDate(timestamp));
    }
}

export class ChartModel {
    row: RowModel;
    columns: Array<ColumnModel>;
    selectStart: number;
    selectEnd: number;
    yScaled: ?boolean;
    stacked: ?boolean;
    stack: Array;
    constructor (data: ChartData) {
        this.yScaled = data.y_scaled;
        this.stacked = data.stacked;
        this.columns = [];
        this.stack = [];
        data.columns.forEach(col => {
            const key: string = col[0];
            const _data = col.slice(1);
            switch (data.types[key]) {
            case 'bar':
            case 'line':
            case 'area':
                const column = new ColumnModel(key, _data, data.types[key], data.names[key], data.colors[key])
                this.columns.push(column);
                if (this.stacked && column.type === 'bar') {
                    _data.forEach((val, index) => {
                        let stackItem = this.stack[index];
                        if (!stackItem) {
                            stackItem = this.stack[index] = []
                        }
                        stackItem.push({ column, val });
                    });
                }
                break;
            case 'x':
                this.row = new RowModel(key, _data, 'x');
                break;
            }
        });
        this.selectStart = Math.floor((this.row.data.length / 6) * 5);
        this.selectEnd = this.row.data.length;
        if (this.stacked) {
            if (this.stack.length) {
                this.stack.forEach(item => {
                    item.sort((a, b) => {
                        if (a.val > b.val) return -1;
                        if (a.val < b.val) return 1;
                        return 0;
                    })
                })
            } else {
                this.columns.sort((a, b) => {
                    if (a.max > b.max) return -1;
                    if (a.max < b.max) return 1;
                    return 0;
                })
            }
        }
    }

    getMaxSelectionValue () {
        return maxValue(this.columns.filter(c => !c._disabled).map(c => {
            const selection = c.data.slice(this.selectStart, this.selectEnd);
            return maxValue(selection);
        }));
    }

    getMaxValue () {
        return maxValue(this.columns.filter(c => !c._disabled).map(c => c.max));
    }

    getMaxColumnValue (columnKey: string) {
        return maxValue(this.columns.filter(c => c.key === columnKey).map(c => {
            const selection = c.data.slice(this.selectStart, this.selectEnd);
            return maxValue(selection);
        }));
    }
}
